// src/components/settings/PetSettings.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { referenceDataAPI, PetReferenceUsage } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useTabQueryParam } from '../../hooks/useTabQueryParam';
import SettingsPagePetsSubMenu from '../menus/SettingsPagePetsSubMenu';
import PetTypesTab from './PetTypesTab';
import PetSizesTab from './PetSizesTab';
import PetPricesTab from './PetPricesTab';
import PetBreedsTab from './PetBreedsTab';
import PetColoursTab from './PetColoursTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const PET_TABS = [
  { label: 'Pet Types', minWidth: '130px' },
  { label: 'Sizes', minWidth: '120px' },
  { label: 'Breeds', minWidth: '120px' },
  { label: 'Colours', minWidth: '120px' },
  { label: 'Prices', minWidth: '120px' },
];

// Exported so SettingsPage's "PRICES" shortcut can target this exact tab by
// id (via the same `petSubTab` URL param this component reads/writes
// itself) without either file hardcoding a numeric index the other has to
// stay in sync with.
export const PET_SUB_TAB_IDS = [
  'types',
  'sizes',
  'breeds',
  'colours',
  'prices',
] as const;
export type PetSubTabId = (typeof PET_SUB_TAB_IDS)[number];

export const petSubTabIdToIndex = (id: PetSubTabId): number =>
  PET_SUB_TAB_IDS.indexOf(id);
export const petSubTabIndexToId = (index: number): PetSubTabId =>
  PET_SUB_TAB_IDS[index] ?? PET_SUB_TAB_IDS[0];

const EMPTY_USAGE: PetReferenceUsage = {
  petTypes: {},
  petSizes: {},
  petBreeds: {},
  petColours: {},
};

interface PetSettingsProps {
  forceTab?: number;
}

const PetSettings: React.FC<PetSettingsProps> = ({ forceTab }) => {
  // Sub-tab is persisted to the `petSubTab` URL param so a refresh (or a
  // deep link) restores whichever pet sub-tab was open - independently of
  // whatever SettingsPage is doing. `forceTab` - set only by SettingsPage's
  // "PRICES" shortcut - takes precedence when provided and is written
  // straight through to the same param, so it survives a subsequent
  // refresh too rather than only holding for this render.
  const [subTabId, setSubTabId] = useTabQueryParam<PetSubTabId>(
    'petSubTab',
    PET_SUB_TAB_IDS,
    'types'
  );
  const selectedTab = petSubTabIdToIndex(subTabId);

  useEffect(() => {
    if (forceTab !== undefined) {
      setSubTabId(petSubTabIndexToId(forceTab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTab]);

  const [usage, setUsage] = useState<PetReferenceUsage>(EMPTY_USAGE);
  const { handleError } = useErrorHandler();

  const fetchUsage = useCallback(async () => {
    try {
      setUsage(await referenceDataAPI.getPetReferenceUsage());
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return (
    <Box>
      <SettingsPagePetsSubMenu
        tabs={PET_TABS}
        activeTab={selectedTab}
        onTabChange={(index: number) => setSubTabId(petSubTabIndexToId(index))}
      />

      <Box>
        <TabPanel value={selectedTab} index={0}>
          <PetTypesTab usage={usage.petTypes} onDataChanged={fetchUsage} />
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <PetSizesTab usage={usage.petSizes} onDataChanged={fetchUsage} />
        </TabPanel>
        <TabPanel value={selectedTab} index={2}>
          <PetBreedsTab usage={usage.petBreeds} onDataChanged={fetchUsage} />
        </TabPanel>
        <TabPanel value={selectedTab} index={3}>
          <PetColoursTab usage={usage.petColours} onDataChanged={fetchUsage} />
        </TabPanel>
        <TabPanel value={selectedTab} index={4}>
          <PetPricesTab />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default PetSettings;
